import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Injector, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { LoanPurpose } from '../../../models/enums/loan-purpose.enum';
import { LtvValueBasis } from '../../../models/enums/ltv-value-basis.enum';
import { LoanType } from '../../../models/loan-type-model';
import { GovernmentLoanDetail, MortgageApplication } from '../../../models/mortgage-app.model';
import { TransactionDetail } from '../../../models/transaction-detail.model';
import { TypeOfLoanStep } from '../../../models/wizard/type-of-loan-step.model';
import { EnumsService } from '../../../services/enums.service';
import { WizardStepComponentBase } from '../wizard-step-base.component';
import { SubjectProperty } from '../../../models/subject-property.model';
import { Address } from '../../../models/address-model';
import { AddressComponent } from '../../address/address.component';
import { initialCashGiftDownPayment } from '../asset-dialog/cash-gift-down-payment';

@Component({
    selector: 'type-of-loan-step',
    templateUrl: 'type-of-loan-step.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TypeOfLoanStepComponent
  extends WizardStepComponentBase<TypeOfLoanStep>
  implements OnInit {

  @ViewChild('childCareProviderAddressComponent')
  childCareProviderAddressComponent: AddressComponent | undefined;

  @ViewChild('loanInfoForm')
  loanInfoForm: NgForm | undefined;

  loanTypes: LoanType[] = [];

  loanTypeId: string | undefined;

  estimatedLoanAmount: number | undefined;

  downpayment: number | undefined;

  defaultLoanType: LoanType | undefined;

  mortgage: MortgageApplication;

  protected isRefinance: boolean = false;

  protected isMobile: boolean = false;

  protected homeValue: number;

  protected mortgageAppliedForType: string | undefined;

  protected hasChildCareExpenses: boolean | undefined | null = null;

  protected childCareProviderAddress: Address | undefined = new Address();

  // Child care expense fields
  protected childCareProviderName: string | undefined;
  protected childCareCostPerMonth: number | undefined;

  constructor(
    private readonly _injector: Injector,
    private readonly _enumsService: EnumsService,
    private readonly _cdr: ChangeDetectorRef
  ) {
    super(_injector);
    if (!this.mortgageApplication.transactionDetail) {
      this.mortgageApplication.transactionDetail = new TransactionDetail();
    }
    if (!this.mortgageApplication.subjectProperty) {
      this.mortgageApplication.subjectProperty = new SubjectProperty();
    }
    if (!this.mortgageApplication.governmentLoanDetail) {
      this.mortgageApplication.governmentLoanDetail = new GovernmentLoanDetail();
    }
    this.setChildCareAddress();

    this.saveMortgageApplicationBeforeNextStep = true;
    this.loadLoanTypes();
    this.estimatedLoanAmount = this.mortgageApplication.mortgageTerm.amount;
    this.homeValue = this.calculateHomeValue(this.mortgageApplication);
    this.downpayment = this.calculateDownPayment(this.mortgageApplication);
    this.prefillDownPaymentFromCashGifts();
    this.mortgage = this.mortgageApplication;
    if (!this.mortgage.subjectProperty.downPaymentSource) {
      this.mortgage.subjectProperty.downPaymentSource = null;
    }
    if (!this.mortgage.mortgageTerm.mortgageAppliedFor) {
      this.mortgage.mortgageTerm.mortgageAppliedFor = null;
      this.loanTypeId = null;
    } else {
      this.mortgageAppliedForType = this.mortgage.mortgageTerm.mortgageAppliedFor;
      this.setIfHasChildCareInfo();
    }
  }

  public loadLoanTypes() {
    if (this.wizardFlowService.isEditEnabled) {
      this.setupSomeDefaultDataForEditMode();
      return;
    }
    this.mortgageApplicationService
      .getLoanTypes(this.mortgageApplication.applicationId)
      .subscribe((loanTypes) => {
        const sortedLoanTypes = loanTypes.sort((a, b) =>
          a.order! > b.order! ? 1 : -1
        );
        this.loanTypes = sortedLoanTypes.filter(
          (lt) => lt.mortgageAppliedForType != null
        );

        this.defaultLoanType = this.loanTypes.find(
          (lt) => lt.defaultForOnlineApp
        );

        const mortgageType = this._enumsService.mortgageTypes.find(
          (mt) =>
            mt.value == this.mortgageApplication.mortgageTerm.mortgageAppliedFor
        );
        const selectedLoanType = this.loanTypes.find(
          (lt) => lt.mortgageAppliedForType == mortgageType?.name
        );
        if (selectedLoanType) {
          this.loanTypeId = selectedLoanType.loanTypeId?.toString();
        }
        this._cdr.markForCheck();
      });
  }

  public get downPaymentSourceTypes(): any[] {
    return this._enumsService.downPaymentSourceTypes.filter(x => x.name != "Cash On Hand");
  }

  public get estimatedLTV(): number | undefined {
    const ltvValueBasis = this.loanTypes.find(
      lt => lt.loanTypeId?.toString() === this.loanTypeId
    )?.ltvValueBasis;

    if (
      this.mortgageApplication.subjectProperty.purposeOfLoan ===
      LoanPurpose.Refinance
    ) {
      const loanAmount = this.mortgageApplication!.mortgageTerm!.amount;
      // Appraised value supersedes the estimated (present) value when provided (LLS-218), matching the
      // property-value convention used elsewhere (minOfSalesPriceAndAppraisedValue / URLA calc service).
      const estValue = this.mortgageApplication!.mortgageTerm?.appraisedValue ?? this.mortgageApplication!.subjectProperty!.presentValue;
      if (!loanAmount || loanAmount === 0 || !estValue || estValue === 0) {
        return undefined;
      }
      return Math.round((loanAmount / estValue) * 100);
    } else {
      const purchasePriceAmount: number =
        this.mortgageApplication!.transactionDetail!.purchasePriceAmount!;
      if (!purchasePriceAmount || purchasePriceAmount === 0) {
        return undefined;
      }
      let denominator: number | undefined | null;
      if (ltvValueBasis === LtvValueBasis.AppraisedValue) {
        denominator =
          this.mortgageApplication!.mortgageTerm?.appraisedValue ||
          this.mortgageApplication!.subjectProperty!.presentValue;
      } else if (ltvValueBasis === LtvValueBasis.PurchasePrice) {
        if (
          this.mortgageApplication!.subjectProperty!.purposeOfLoan ===
            LoanPurpose.Purchase &&
          purchasePriceAmount
        ) {
          denominator = purchasePriceAmount;
        } else {
          denominator =
            this.mortgageApplication!.mortgageTerm?.appraisedValue ||
            this.mortgageApplication!.subjectProperty!.presentValue;
        }
      } else {
        denominator = purchasePriceAmount;
      }
      if (!denominator || denominator === 0) {
        return undefined;
      }
      const loanAmount =
        this.mortgageApplication!.transactionDetail!.purchasePriceAmount! -
        this.downpayment!;
      return Math.round(
        (loanAmount / denominator) * 100
      );
    }
  }

  onLoanTypeChanged = () => {
    const selectedLoanType = this.loanTypes.find(
      (lt) => lt.loanTypeId.toString() == this.loanTypeId
    );
    if (selectedLoanType) {
      this.mortgageAppliedForType = selectedLoanType.mortgageAppliedForType;
    } else {
      this.mortgageAppliedForType = null;
    }
    if (this.mortgageAppliedForType !== 'VA') {
      this.hasChildCareExpenses = null;
    }
  }

  onEstimatedValueChanged = () => {
    if (!this.mortgageApplication.subjectProperty.presentValue) {
      this.mortgageApplication.subjectProperty.presentValue = undefined;
    }
  };

  onAppraisedValueChanged = () => {
    if (!this.mortgageApplication.mortgageTerm.appraisedValue) {
      this.mortgageApplication.mortgageTerm.appraisedValue = undefined;
    }
    // Appraised value participates in the property-value calc (appraisedValue ?? presentValue), so keep
    // the derived home value in sync when it changes (LLS-218). On refinance the Loan Amount is entered
    // by the borrower, so only the purchase flow recomputes the estimated loan amount.
    this.homeValue = this.calculateHomeValue(this.mortgageApplication);
    if (!this.isRefinance) {
      this.estimatedLoanAmount = this.calculateEstimatedLoanAmount(this.mortgageApplication);
    }
  };

  onLoanAmountChanged = () => {
    if (!this.estimatedLoanAmount) {
      this.estimatedLoanAmount = undefined;
    }
  };

  onNextClicked() {
    if (this.loanInfoForm) {
      this.loanInfoForm.form.markAllAsTouched();
      let isChildCareAddressValid = true;
      if (this.childCareProviderAddressComponent) {
        isChildCareAddressValid = this.childCareProviderAddressComponent.validate();
      }
      if (this.loanInfoForm.form.valid && isChildCareAddressValid) {

        this.setChildCareAddressOnMortgage();

        this.mortgageApplication.mortgageTerm.amount = this.estimatedLoanAmount;
        if (this.wizardFlowService.isEditEnabled || !this.loanTypeId) {
          this.mortgageApplication.mortgageTerm.mortgageAppliedFor = null;
          super.onNextClicked();
          return;
        }
        if (this.loanTypeId === '-1') {
          if (this.defaultLoanType) {
            this.mortgageApplication.mortgageTerm.mortgageAppliedFor =
              this.defaultLoanType.mortgageAppliedForType;
          }
          super.onNextClicked();
          return;
        }
        this.startSpinner();
        this.mortgageApplicationService
          .saveLoanType(
            this.mortgageApplication.applicationId,
            this.loanTypeId!
          )
          .subscribe(
            () => {
              this.stopSpinner();
              this.mortgageApplication.mortgageTerm.mortgageAppliedFor = this.mortgageAppliedForType;
              super.onNextClicked();
            },
            (error) => {
              this.stopSpinner();
            }
          );
      }
    }
  }

  ngOnInit() {
    // This is just to make the validation work properly
    if (
      this.mortgageApplication.transactionDetail.purchasePriceAmount != null &&
      this.mortgageApplication.transactionDetail.purchasePriceAmount <= 0
    ) {
      this.mortgageApplication.transactionDetail.purchasePriceAmount =
        undefined;
    }
    this.isRefinance = this.mortgage.subjectProperty.purposeOfLoan === 'Refinance';

    this.configureBasedOnScreenSize(window.innerWidth);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.configureBasedOnScreenSize(window.innerWidth);
  }

  onPurchasePriceChanged = () => {
    if (
      this.mortgageApplication.transactionDetail.purchasePriceAmount != null &&
      this.mortgageApplication.transactionDetail.purchasePriceAmount <= 0
    ) {
      this.mortgageApplication.transactionDetail.purchasePriceAmount =
        undefined;
    }
    this.homeValue = this.calculateHomeValue(this.mortgageApplication);
    this.estimatedLoanAmount = this.calculateEstimatedLoanAmount(this.mortgageApplication);
  };

  onDownPaymentChanged = () => {
    if (!this.downpayment) {
      this.downpayment = undefined;
      this.mortgageApplication.subjectProperty.downPaymentSource = null;
    }
    this.estimatedLoanAmount = this.calculateEstimatedLoanAmount(this.mortgageApplication);
  };

  setupSomeDefaultDataForEditMode = () => {
    const conventional = new LoanType();
    conventional.loanTypeName = 'Conventional';
    conventional.mortgageAppliedForType = 'Conventional';
    conventional.order = 1;
    conventional.loanTypeId = 1;

    const fha = new LoanType();
    fha.loanTypeName = 'FHA';
    fha.mortgageAppliedForType = 'FHA';
    fha.order = 2;
    fha.loanTypeId = 2;

    const va = new LoanType();
    va.loanTypeName = 'VA';
    va.mortgageAppliedForType = 'VA';
    va.order = 3;
    va.loanTypeId = 3;

    const usda = new LoanType();
    usda.loanTypeName = 'USDA';
    usda.mortgageAppliedForType = 'USDA';
    usda.order = 4;
    usda.loanTypeId = 4;

    this.loanTypes.push(conventional);
    this.loanTypes.push(fha);
    this.loanTypes.push(va);
    this.loanTypes.push(usda);

    this.loanTypeId = '1';
  };

  private calculateDownPayment = (mortgage: MortgageApplication) => {
    const homeValue = this.calculateHomeValue(mortgage);
    if (!homeValue || !mortgage.mortgageTerm.amount) {
      return undefined;
    }
    const downPayment = (homeValue ?? 0) - (mortgage.mortgageTerm.amount ?? 0);
    return downPayment;
  }

  // LLS-641: the Down Payment is at least the total of the Cash Gifts the borrower said
  // are for the down payment (see initialCashGiftDownPayment).
  private prefillDownPaymentFromCashGifts() {
    const giftDownPayment = initialCashGiftDownPayment(this.mortgageApplication, this.inCoApplyFlow, this.downpayment);
    if (giftDownPayment === undefined) {
      return;
    }
    this.downpayment = giftDownPayment;
    if (this.homeValue > this.downpayment) {
      this.estimatedLoanAmount = this.calculateEstimatedLoanAmount(this.mortgageApplication);
    }
  }

  private setIfHasChildCareInfo() {
    this.hasChildCareExpenses = false;
    if (this.mortgageAppliedForType === 'VA') {
      const hasChildCareExpenses = !!this.mortgageApplication.governmentLoanDetail.childCareCostPerMonth
       || !!this.mortgageApplication.governmentLoanDetail.childCareProviderAddress
       || !!this.mortgageApplication.governmentLoanDetail.childCareProviderCity
       || !!this.mortgageApplication.governmentLoanDetail.childCareProviderState
       || !!this.mortgageApplication.governmentLoanDetail.childCareProviderZip;
      this.hasChildCareExpenses = hasChildCareExpenses;
    }
  }

  private calculateEstimatedLoanAmount(mortgage: MortgageApplication): number {
    const homeValue = this.calculateHomeValue(mortgage);
    return (
      homeValue - this.downpayment!
    );
  }

  private configureBasedOnScreenSize(windowWidth: number) {
    this.isMobile = windowWidth < 768
  }

  private calculateHomeValue = (mortgage: MortgageApplication) => {
    let minValue = this.minOfSalesPriceAndAppraisedValue(mortgage);
    if (!minValue) {
      minValue = 0;
    }
    return minValue;
  }

  private setChildCareAddress() {
    this.childCareProviderAddress.address1 = this.mortgageApplication.governmentLoanDetail.childCareProviderAddress;
    this.childCareProviderAddress.city = this.mortgageApplication.governmentLoanDetail.childCareProviderCity;
    this.childCareProviderAddress.state = this.mortgageApplication.governmentLoanDetail.childCareProviderState;
    this.childCareProviderAddress.zipCode = this.mortgageApplication.governmentLoanDetail.childCareProviderZip;
  }

  private setChildCareAddressOnMortgage() {
    this.mortgageApplication.governmentLoanDetail.childCareProviderAddress = this.childCareProviderAddress.address1;
    this.mortgageApplication.governmentLoanDetail.childCareProviderCity = this.childCareProviderAddress.city;
    this.mortgageApplication.governmentLoanDetail.childCareProviderState = this.childCareProviderAddress.state;
    this.mortgageApplication.governmentLoanDetail.childCareProviderZip = this.childCareProviderAddress.zipCode;
  }

  private minOfSalesPriceAndAppraisedValue = (mortgage: MortgageApplication): number => {
    const presentValue = mortgage.mortgageTerm?.appraisedValue ?? mortgage.subjectProperty?.presentValue ?? 0;
    const purchasePriceAmount = mortgage.transactionDetail?.purchasePriceAmount ?? 0;

    if (mortgage.subjectProperty.purposeOfLoan === 'Purchase') {
      const salesPrice = purchasePriceAmount ? purchasePriceAmount : Number.MAX_VALUE;
      const appraisedValue = presentValue ? presentValue : Number.MAX_VALUE;
      const min = Math.min(salesPrice, appraisedValue);
      return min != Number.MAX_VALUE ? min : 0;
    } else {
      // Refinance
      return presentValue;
    }
  };
}
